import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCalendarAlt, 
  faPencilAlt, 
  faTrashAlt, 
  faArrowLeft, 
  faUser
} from '@fortawesome/free-solid-svg-icons';
import AdminLayout from './AdminLayout';
import { ADMIN_URLS } from '../../config/apiConfig';

function ViewOfficeBearer() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [bearer, setBearer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  useEffect(() => {
    fetchOfficeBearer();
  }, [id]);
  
  const fetchOfficeBearer = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARER_DETAIL(id), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        throw new Error('Failed to fetch office bearer details');
      }
      
      const data = await response.json();
      setBearer(data);
    } catch (error) {
      console.error('Error fetching office bearer:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDelete = () => {
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      const response = await fetch(ADMIN_URLS.OFFICE_BEARER_DETAIL(id), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          navigate('/admin/login', { replace: true });
          return;
        }
        throw new Error('Failed to delete office bearer');
      }
      
      navigate('/admin/office-bearers');
    } catch (error) {
      console.error('Error deleting office bearer:', error);
      alert(`Failed to delete office bearer: ${error.message}`);
      setShowDeleteModal(false);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'Present';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };
  
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading...</div>
      </AdminLayout>
    );
  }

  if (error || !bearer) {
    return (
      <AdminLayout>
        <div className="admin-page-header">
          <h1 className="admin-page-title">Office Bearer Details</h1>
          <ul className="admin-breadcrumb">
            <li className="admin-breadcrumb-item">
              <Link to="/admin/dashboard">Dashboard</Link>
            </li>
            <li className="admin-breadcrumb-item">
              <Link to="/admin/office-bearers">Office Bearers</Link>
            </li>
            <li className="admin-breadcrumb-item active">View Office Bearer</li>
          </ul>
        </div>
        
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '1rem', 
          borderRadius: '0.25rem',
          marginBottom: '1rem' 
        }}>
          {error || 'Office bearer not found'}
          <div style={{ marginTop: '1rem' }}>
            <button 
              className="admin-btn admin-btn-primary" 
              onClick={() => navigate('/admin/office-bearers')}
            >
              Back to Office Bearers
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Office Bearer Details</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/office-bearers">Office Bearers</Link>
          </li>
          <li className="admin-breadcrumb-item active">{bearer.name}</li>
        </ul>
      </div>
      
      <div className="admin-actions" style={{ marginBottom: '1.5rem' }}>
        <Link to="/admin/office-bearers" className="admin-btn admin-btn-light admin-btn-with-icon">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>Back to Office Bearers</span>
        </Link>
        <div>
          <Link to={`/admin/office-bearers/${id}/edit`} className="admin-btn admin-btn-primary admin-btn-with-icon" style={{ marginRight: '0.5rem' }}>
            <FontAwesomeIcon icon={faPencilAlt} />
            <span>Edit</span>
          </Link>
          <button onClick={handleDelete} className="admin-btn admin-btn-danger admin-btn-with-icon">
            <FontAwesomeIcon icon={faTrashAlt} />
            <span>Delete</span>
          </button>
        </div>
      </div>
      
      <div className="admin-row" style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -0.75rem' }}>
        <div className="admin-col" style={{ flex: '1 1 400px', padding: '0 0.75rem', marginBottom: '1.5rem' }}>
          <div className="admin-card" style={{ height: '100%' }}>
            <div className="admin-card-header">
              <h2 className="admin-card-title">Office Bearer Information</h2>
            </div>
            <div className="admin-card-body">
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ 
                  width: '150px',
                  height: '150px',
                  borderRadius: '50%',
                  backgroundImage: `url(${bearer.image_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundColor: '#e9ecef',
                  border: '1px solid #ced4da',
                  flexShrink: 0
                }}></div>
                
                <div style={{ flex: '1' }}>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>{bearer.name}</h3>
                  <p style={{ 
                    fontSize: '1.1rem', 
                    fontWeight: '500', 
                    color: 'var(--admin-primary)',
                    marginBottom: '1rem'
                  }}>
                    {bearer.position}
                  </p>
                  
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '0.75rem', color: 'var(--admin-text-muted)' }} />
                    <span>
                      <strong>Term:</strong> {formatDate(bearer.term_start)} - {formatDate(bearer.term_end)}
                    </span>
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.75rem', color: 'var(--admin-text-muted)' }} />
                    <span>
                      <strong>ID:</strong> {bearer.id}
                    </span>
                  </div>
                </div>
              </div>
              
              <div style={{ marginTop: '1.5rem' }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.75rem' }}>Bio</h4>
                <p style={{ lineHeight: '1.6' }}>{bearer.bio}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="admin-col" style={{ flex: '1 1 300px', padding: '0 0.75rem', marginBottom: '1.5rem' }}>
          <div className="admin-card" style={{ height: '100%' }}>
            <div className="admin-card-header">
              <h2 className="admin-card-title">Additional Information</h2>
            </div>
            <div className="admin-card-body">
              <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) 2fr', rowGap: '0.75rem', columnGap: '1rem' }}>
                <dt>Created At:</dt>
                <dd>{formatDate(bearer.created_at)}</dd>
                
                <dt>Updated At:</dt>
                <dd>{formatDate(bearer.updated_at)}</dd>
              </dl>
              
              <div style={{ marginTop: '2rem' }}>
                <h4 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.75rem' }}>Preview</h4>
                <p style={{ marginBottom: '1rem' }}>This is how the office bearer appears on the public website:</p>
                
                <div style={{ 
                  border: '1px solid #ced4da',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    height: '150px',
                    backgroundImage: `url(${bearer.image_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}></div>
                  <div style={{ padding: '1rem' }}>
                    <h3 style={{ fontSize: '1.2rem', marginBottom: '0.3rem', color: 'var(--admin-primary)' }}>
                      {bearer.name}
                    </h3>
                    <p style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: '#6c757d',
                      marginBottom: '0.75rem'
                    }}>
                      {bearer.position}
                    </p>
                    <p style={{ fontSize: '0.875rem', lineHeight: '1.5', maxHeight: '60px', overflow: 'hidden' }}>
                      {bearer.bio}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '2rem',
            width: '100%',
            maxWidth: '500px',
            boxShadow: 'var(--admin-shadow)'
          }}>
            <h3 style={{ marginBottom: '1rem' }}>Confirm Deletion</h3>
            <p style={{ marginBottom: '1.5rem' }}>
              Are you sure you want to delete <strong>{bearer.name}</strong> from office bearers?
              This action cannot be undone.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
              <button 
                className="admin-btn admin-btn-light"
                onClick={cancelDelete}
                style={{ backgroundColor: '#f8f9fa', borderColor: '#dee2e6' }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn admin-btn-danger"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ViewOfficeBearer;
